<script lang="ts" setup>
import type { ChatMessageRole } from '~kui/types/chat';

import KUICoachLoaderColouredDots from '~kui/components/KUI/Coach/KUICoachLoaderColouredDots.vue';
import { useI18n } from 'vue-i18n';

interface Props {
  role: ChatMessageRole;
  message?: string;
  loading: boolean;
  direction?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
}

const props = withDefaults(defineProps<Props>(), {
  message: '',
});

const { t } = useI18n();

const variantClasses = computed(() => {
  return twMerge(
    cva('flex flex-col gap-3 px-3.75 py-3', {
      variants: {
        role: {
          assistant: 'bg-white rounded-tl-lg rounded-tr-3xl rounded-br-3xl rounded-bl-3xl max-w-sm',
          user: 'bg-muted rounded-tl-3xl rounded-tr-xl rounded-br-3xl rounded-bl-3xl',
          error: 'border-destructive bg-destructive-subtle rounded-tl-lg rounded-tr-3xl rounded-br-3xl rounded-bl-3xl border',
        },
        direction: {
          'top-left': 'rounded-tl-lg rounded-tr-3xl rounded-br-3xl rounded-bl-3xl',
          'top-right': 'rounded-tl-3xl rounded-tr-lg rounded-br-3xl rounded-bl-3xl',
          'bottom-left': 'rounded-tl-3xl rounded-tr-3xl rounded-br-3xl rounded-bl-lg',
          'bottom-right': 'rounded-tl-3xl rounded-tr-3xl rounded-br-lg rounded-bl-3xl',
        },
      },
    })({
      role: props.role,
      direction: props.direction,
    }),
  );
});
</script>

<template>
  <div :class="variantClasses">
    <span role="status" class="sr-only">{{ loading ? t('unified.common.loadingContent') : '' }}</span>
    <div v-if="loading" class="flex items-center">
      <KUICoachLoaderColouredDots />
    </div>
    <div v-else-if="message" class="flex flex-col items-start">
      <div class="flex items-center gap-2">
        <slot name="prefixContent" />
        <span v-if="role === 'user'">{{ message }}</span>
        <KUICoreMarkdown v-else :markdown="message" />
      </div>
      <div v-if="$slots.extraContent">
        <slot name="extraContent" />
      </div>
    </div>
  </div>
</template>
